// Using static imports for API client
import { createApiClient } from './apiClient';
import tenantConfig, { getBaseUrl, getTenantIdFromHostname } from '../utils/apiConfig';
import axios from 'axios';
import { safeDecodeJWT, getTenantFromJWT } from '../utils/jwtHelpers';
import { AUTH_CONFIG } from '../../config';

// Tenant UUID lookup - sourced from environment variables so they are not
// compiled into the JS bundle. Literal fallbacks preserve existing behaviour
// when env vars are absent (e.g. CI environments without a .env file).
const PROSPANCE_UUID = import.meta.env.VITE_PROSPANCE_TENANT_UUID || '858d9712-b0cb-4d8a-adea-652e3047f48a';
const DEV_UUID = import.meta.env.VITE_DEV_TENANT_UUID || 'd6f953f4-d914-4906-b61e-d48d6b975f74';

const TENANT_ID_MAP: Record<string, string> = {
  'prospance': PROSPANCE_UUID,
  'dev': DEV_UUID,
  'current': PROSPANCE_UUID
};

// Helper function to get tenant UUID from slug
const getTenantUUID = (slug: string): string => {
  return TENANT_ID_MAP[slug] || PROSPANCE_UUID || slug;
};

// Development flag for mock authentication - ONLY for dev tenant
// NOTE: Disabled for localhost to allow real backend testing
const isDevelopmentTenant = () => {
  const hostname = window.location.hostname;
  return hostname === 'dev-brain.astrapeak.ai';
  // Removed localhost check to use real backend in local development
};

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

// 2FA Required Response (APB-989)
export interface TwoFactorRequiredResponse {
  requires_2fa: true;
  pending_token: string;
  available_methods: string[];
  preferred_method?: string;
  otp_sent: boolean;
  otp_destination?: string;
  message: string;
}

// Union type for login response
export type AuthLoginResponse = LoginResponse | TwoFactorRequiredResponse;

// Type guard to check if 2FA is required
export function isTwoFactorRequired(response: AuthLoginResponse): response is TwoFactorRequiredResponse {
  return 'requires_2fa' in response && response.requires_2fa === true;
}

// AuthService compatibility interfaces
// Using snake_case for tenant_id to match backend API contract
interface UserData {
  tenant: string;
  tenant_id: string;  // Backend standard: snake_case
  username: string;
  userId: string;
  roles: string[];
  currentRole: string;
}

interface AuthResponse {
  success: boolean;
  user?: UserData;
  token?: string;
  error?: string;
}

/**
 * Extract tenant_id from JWT token
 * @param token - JWT token
 * @returns tenant_id or null if not found
 */
export function getTenantFromToken(token: string): string | null {
  // Use production-ready JWT helper
  return getTenantFromJWT(token) || null;
}

/**
 * Authentication API service
 */
export const authApi = {
  /**
   * Login with username and password
   * @param username - User's username
   * @param password - User's password
   * @returns Promise with login response containing token and user data
   */
  async login(username: string, password: string): Promise<AuthLoginResponse> {
    // Only use mock authentication for dev tenant
    if (isDevelopmentTenant() && import.meta.env.DEV) {
      console.log('Using mock authentication for development tenant');
      
      // Determine roles based on username
      const roles = [];
      if (username.includes('executive')) roles.push('executive');
      if (username.includes('analyst')) roles.push('analyst');
      if (username.includes('admin')) roles.push('admin');
      
      // If no specific role detected, give analyst role by default
      if (roles.length === 0) roles.push('analyst');
      
      // Create mock user with dev tenant information
      const mockUser = {
        id: `dev-user-${Date.now()}`,
        username,
        roles,
        tenant_id: 'dev'
      };
      
      // Create a mock token for development
      const mockTokenPayload = {
        sub: mockUser.id,
        username: username,
        roles: roles,
        tenant: 'dev',
        exp: Math.floor(Date.now() / 1000) + 3600
      };
      
      console.log('Mock authentication successful for development tenant');
      
      return {
        access_token: btoa(JSON.stringify(mockTokenPayload)),
        refresh_token: btoa(JSON.stringify({...mockTokenPayload, type: 'refresh'})),
        token_type: 'bearer',
        expires_in: 1800
      };
    }

    try {
      // CRITICAL FIX: Ensure proper tenant context for authentication
      // Get tenant slug from hostname or default to dev for localhost
      const hostname = window.location.hostname;
      let tenantSlug;
      
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // For local development, use dev tenant
        tenantSlug = 'dev';
      } else {
        // Extract tenant from hostname (e.g., prospance-brain.astrapeak.ai -> prospance)
        tenantSlug = hostname.split('.')[0].split('-')[0] || 'prospance';
      }
      
      // Get tenant UUID from slug for proper backend identification
      const tenantUUID = getTenantUUID(tenantSlug);
      
      // Use tenant configuration system for consistent API URLs
      const config = tenantConfig();
      
      // Try multiple authentication methods in sequence
      let authError = null;
      let response = null;
      
      // Method 1: Try tenant-specific domain with CloudFront
      try {
        const apiClient = createApiClient(config.apiUrl);
        
        const formData = new URLSearchParams({
          grant_type: 'password',
          username: username,
          password: password,
          scope: `tenant_id:${tenantUUID}`  // BRAIN expects tenant_id: prefix with UUID, not slug
        }).toString();
        
        // CRITICAL FIX: Use correct endpoint path
        // For localhost, apiClient baseURL is http://localhost:8000, so we need /api/auth/token
        const endpoint = '/api/auth/token';
        
        response = await apiClient.post(endpoint, formData, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Tenant-ID': tenantSlug,  // Use tenant slug for consistency with scope
            'Accept': 'application/json'
          },
          withCredentials: true
        });
      } catch (error) {
        console.error('Authentication failed with tenant-specific domain:', error);
        authError = error;
        
        // If tenant-specific domain fails, throw the error
        // No fallback to hardcoded URLs - use apiConfig.ts as single source of truth
        throw authError;
      }
      
      // Validate OAuth2 token response with more lenient checking
      if (!response || !response.data) {
        console.error('Empty response data');
        throw new Error('Authentication response is empty');
      }
      
      // Check if 2FA is required (APB-989)
      if (response.data.requires_2fa === true) {
        console.log('🔐 2FA required for user, returning 2FA response');
        return {
          requires_2fa: true,
          pending_token: response.data.pending_token,
          available_methods: response.data.available_methods || [],
          preferred_method: response.data.preferred_method,
          otp_sent: response.data.otp_sent || false,
          otp_destination: response.data.otp_destination,
          message: response.data.message || 'Two-factor authentication required'
        } as TwoFactorRequiredResponse;
      }
      
      // Check for access token with fallback to token field
      const accessToken = response.data.access_token || response.data.token;
      if (!accessToken) {
        console.error('Invalid OAuth2 response structure:', response.data);
        throw new Error('Authentication response missing token field');
      }
      
      console.log('✅ OAuth2 authentication successful');
      console.log('Token type:', response.data.token_type);
      console.log('Expires in:', response.data.expires_in, 'seconds');
      
      // Extract user data from JWT token payload (not from response.data)
      const tokenPayload = safeDecodeJWT(accessToken);
      if (!tokenPayload) {
        throw new Error('Failed to decode access token');
      }
      
      const userData = {
        id: tokenPayload.sub,
        username: username,
        roles: tokenPayload.roles || ['user'],
        tenant_id: tokenPayload.tenant,
        // Add any other required fields with fallbacks
        email: `${username}@${tenantSlug}.astrapeak.ai`,
        display_name: username
      };
      
      console.log('User data prepared:', userData);
      
      return {
        access_token: accessToken,
        refresh_token: response.data.refresh_token || '',
        token_type: response.data.token_type || 'bearer',
        expires_in: response.data.expires_in || 1800
      };
    } catch (error: any) {
      console.error('OAuth2 authentication failed:', error);
      
      // Enhanced debugging for the exact failure point
      if (error.response) {
        console.error('HTTP Status:', error.response.status);
        console.error('Response Data:', error.response.data);
        console.error('Request URL:', error.config?.url);
        console.error('Request Headers:', error.config?.headers);
        
        // Specific error handling for common OAuth2 failures
        switch (error.response.status) {
          case 404:
            throw new Error('Authentication endpoint not found - check backend configuration');
          case 415:
            throw new Error('Unsupported media type - OAuth2 form data required');
          case 422:
            throw new Error('Invalid OAuth2 request format - missing required parameters');
          case 401:
            throw new Error('Invalid username, password, or tenant');
          default:
            throw new Error(`Authentication failed: ${error.response.data?.detail || 'Unknown error'}`);
        }
      }
      
      throw new Error(error.message || 'Network error during authentication');
    }
  },

  /**
   * DEPRECATED: Use login() method instead
   * This method is kept for backward compatibility only
   * @deprecated Use login() method directly
   */
  async loginWithTenant(username: string, password: string, tenant: string): Promise<AuthResponse> {
    console.warn('loginWithTenant() is deprecated. Use login() method instead.');
    try {
      // Call the standard login method
      const loginResponse = await this.login(username, password);
      
      // Convert LoginResponse to AuthResponse format
      const tokenPayload = safeDecodeJWT(loginResponse.access_token);
      if (!tokenPayload) {
        throw new Error('Failed to decode access token');
      }
      
      const tenantSlug = window.location.hostname.split('.')[0].split('-')[0] || 'prospance';
      
      const userData: UserData = {
        userId: tokenPayload.sub,
        username: username,
        tenant: tenantSlug,
        tenant_id: tokenPayload.tenant || tokenPayload.tenant_id,
        roles: tokenPayload.roles || ['analyst'],
        currentRole: (tokenPayload.roles && tokenPayload.roles[0]) || 'analyst'
      };
      
      // DO NOT store tokens here - let AuthContext handle state management
      // This prevents session sync issues
      
      return {
        success: true,
        user: userData,
        token: loginResponse.access_token,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Authentication failed'
      };
    }
  },

  /**
   * Synchronous logout wrapper (AuthService compatibility)
   */
  logoutSync(): void {
    this.logout().catch(console.error);
  },

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    // Get token before clearing
    const token = localStorage.getItem('astrapeak_auth_token');
    
    // Clear auth token and all related user/session data
    localStorage.removeItem('astrapeak_auth_token');
    localStorage.removeItem(AUTH_CONFIG.refreshTokenStorageKey);
    localStorage.removeItem(AUTH_CONFIG.tokenExpiryKey);
    localStorage.removeItem('user');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('login_username');
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('sso_session_id');
    sessionStorage.removeItem('authed');
    
    // Call BRAIN logout endpoint if token exists
    if (token) {
      try {
        // Use tenant configuration system for consistent API URLs
        const config = tenantConfig();
        
        // Create API client with tenant-specific domain
        const apiClient = createApiClient(config.apiUrl);
        
        // No explicit logout endpoint in backend, just clear tokens locally
        // If backend adds logout endpoint later, uncomment this
        /*await apiClient.post('/auth/logout', {}, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });*/
      } catch (error) {
        console.error('Logout API call failed:', error);
        // Continue with local logout even if API call fails
      }
    }
  },

  /**
   * Check if user is authenticated
   * @returns Boolean indicating if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('astrapeak_auth_token');
    const user = localStorage.getItem('user');
    return !!token && !!user;
  },

  /**
   * Get current user roles
   * @returns Array of role strings or empty array if not authenticated
   */
  getUserRoles(): string[] {
    const userStr = localStorage.getItem('user');
    if (!userStr) return [];
    
    try {
      const user = JSON.parse(userStr);
      return user.roles || [];
    } catch (e) {
      console.error('Error parsing user data:', e);
      return [];
    }
  },

  /**
   * Get authentication token
   * @returns Token string or null if not authenticated
   */
  getToken(): string | null {
    return localStorage.getItem('astrapeak_auth_token');
  },

  /**
   * Get current user data
   * @returns User data object or null if not authenticated
   */
  getUser(): any | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    
    try {
      const user = JSON.parse(userStr);
      // Validate required fields for authService compatibility
      const missingFields = [];
      if (!user.tenant) missingFields.push('tenant');
      if (!user.tenant_id) missingFields.push('tenant_id');
      if (!user.username) missingFields.push('username');
      if (!user.userId) missingFields.push('userId');
      if (!user.roles) missingFields.push('roles');
      if (!user.currentRole) missingFields.push('currentRole');
      
      if (missingFields.length > 0) {
        console.error('User data missing required fields:', missingFields);
        console.error('Current user data:', user);
        return null;
      }
      return user;
    } catch (e) {
      console.error('Error parsing user data:', e);
      return null;
    }
  },

  /**
   * Validate tenant (for compatibility with authService)
   * @param tenant Tenant slug to validate
   * @returns Promise with validation result
   */
  async validateTenant(tenant: string): Promise<{success: boolean; message?: string}> {
    try {
      // Simple tenant validation - check if it's in our known tenants
      const knownTenants = ['prospance', 'dev', 'current'];
      const isValid = knownTenants.includes(tenant.toLowerCase());
      
      return {
        success: isValid,
        message: isValid ? 'Tenant is valid' : 'Unknown tenant'
      };
    } catch (error) {
      console.error('Tenant validation error:', error);
      return {
        success: false,
        message: 'Tenant validation failed'
      };
    }
  },

  /**
   * Get base URL for API requests (for compatibility with authService)
   * Uses apiConfig.ts as single source of truth
   */
  get baseURL(): string {
    return getBaseUrl();
  }
};

export default authApi;
